import React from 'react';
import { MatchStatus, UserType } from '@gopeerproject/chuck';
import { View } from 'react-native';
import { useStyleSheet, useThemeContext } from '../../../hooks';
import { createStyleSheet, formatMessage } from '../../../utils';
import { Text } from '../../Text';
import { User } from '../../User';
import { Divider } from '../../Divider';
import { Button } from '../../Button';
import { Focusable } from '../../Focusable';
import { Loader } from '../../Loader';
import { Badge } from './Badge';
import moment from 'moment';
import { IconButton } from '../../IconButton';
function customFromNow(date) {
    const now = moment();
    const then = moment(date);
    let diffMinutes = now.diff(then, 'minutes');
    let diffHours = now.diff(then, 'hours');
    let diffDays = now.diff(then, 'days');
    if (diffMinutes < 60) {
        if (diffMinutes < 1)
            diffMinutes = 1;
        return `${diffMinutes}m ago`;
    }
    if (diffHours < 24) {
        if (diffHours < 1)
            diffHours = 1;
        return `${diffHours}h ago`;
    }
    if (diffDays < 1)
        diffDays = 1;
    return `${diffDays}d ago`;
}
export function Instant(props) {
    var _a;
    const { color } = useThemeContext();
    const styles = useStyleSheet(getStyles);
    const isResolved = props.status === MatchStatus.Resolved;
    const isPending = props.status === MatchStatus.Pending;
    const isInactive = props.status === MatchStatus.Active
        ? props.participantStatus === 'inactive'
        : false;
    const isAbandoned = props.status === 'abandoned';
    const isCanceled = props.status === 'canceled';
    return (React.createElement(Focusable, { style: [styles.focusable, props.fluid ? styles.fluid : {}], hoverStyle: styles.hoverStyle, pressStyle: styles.pressStyle, disabledStyle: styles.disabled, disabled: props.disabled, onPress: props.onPress },
        React.createElement(View, { style: [styles.container, props.fluid ? styles.fluid : {}, props.style] },
            React.createElement(View, { style: styles.subjectWrapper },
                React.createElement(Text, { type: 'headline', size: 'lg' }, props.subject),
                React.createElement(Badge, { status: props.status, selfStatus: props.status === MatchStatus.Active
                        ? props.participantStatus
                        : undefined })),
            React.createElement(Text, { type: 'body', size: 'md', weight: 'regular', numberOfLines: 2, ellipsizeMode: 'tail', style: styles.message }, props.message
                ? formatMessage(Object.assign(Object.assign({}, props.message), { author: (_a = props.name) !== null && _a !== void 0 ? _a : '' }))
                : 'Description not provided'),
            React.createElement(View, { style: styles.main }, isPending ? (React.createElement(React.Fragment, null,
                React.createElement(View, { style: { flexDirection: 'row', alignItems: 'center' } },
                    React.createElement(View, { style: styles.loaderWrapper },
                        React.createElement(Loader, { size: 'small', color: color.pending.solid })),
                    React.createElement(Text, { type: 'body', size: 'sm', weight: 'semibold', style: styles.searchingText }, "Searching for a tutor")),
                React.createElement(Button, { type: 'secondary', accent: 'neutral', onPress: props.onCancel, size: 'sm', text: 'Cancel' }))) : isCanceled ? (React.createElement(React.Fragment, null,
                React.createElement(View, { style: { flexDirection: 'row', alignItems: 'center' } },
                    React.createElement(IconButton, { type: 'secondary', accent: 'neutral', size: 'xs', value: 'people-prohibited', onPress: () => { }, style: { marginRight: 8 } }),
                    React.createElement(Text, { type: 'body', size: 'sm', weight: 'semibold', style: styles.searchingText }, "Canceled by you"),
                    React.createElement(Divider, { type: 'vertical', style: styles.dividerHorizontal }),
                    React.createElement(Text, { type: 'body', size: 'md', weight: 'regular', style: styles.timeInfo }, customFromNow(props.createdAt))),
                React.createElement(IconButton, { type: 'secondary', accent: 'neutral', size: 'xs', value: 'chevron-right', onPress: props.onPress }))) : (React.createElement(React.Fragment, null,
                React.createElement(View, { style: styles.tutorContainer },
                    React.createElement(User, { size: 'sm', image: props.image, name: props.name, type: props.profileType }),
                    React.createElement(Focusable, { style: styles.nameWrapper, onPress: () => {
                            if (props.name && props.profileType === UserType.TUTOR)
                                props.onCheckoutProfile();
                        } },
                        React.createElement(Text, { type: 'body', size: 'md', weight: 'semibold', numberOfLines: 1 }, props.name),
                        React.createElement(Divider, { type: 'vertical', style: styles.dividerHorizontal }),
                        React.createElement(Text, { type: 'body', size: 'md', weight: 'regular', style: styles.timeInfo }, customFromNow(props.createdAt)))),
                props.actionButton ? (props.actionButton) : isResolved || isAbandoned || isInactive ? (React.createElement(IconButton, { type: 'secondary', accent: 'neutral', size: 'xs', value: 'chevron-right', onPress: props.onPress })) : (React.createElement(Button, { type: 'primary', accent: 'default', onPress: props.onPress, size: 'sm', text: 'Join Chat' }))))))));
}
const getStyles = createStyleSheet(({ color, size }) => ({
    focusable: {
        borderRadius: size.G
    },
    container: {
        padding: size.G,
        borderRadius: size.G
    },
    hoverStyle: {
        backgroundColor: color.invert2.p10,
        borderRadius: size.G
    },
    pressStyle: {
        opacity: 0.68
    },
    disabled: {
        opacity: 0.42
    },
    fluid: { flex: 1 },
    message: {
        marginTop: size.C,
        color: color.invert2.p68,
        width: size.P * 3 + size.C
    },
    divider: { marginVertical: size.G },
    dividerHorizontal: { marginHorizontal: size.C },
    timeInfo: { color: color.invert2.p56 },
    subTitle: { color: color.invert2.p56, marginBottom: size.C },
    main: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: size.J,
        justifyContent: 'space-between'
    },
    subject: {
        color: color.invert2.p56,
        marginTop: size.A
    },
    viewProfile: { color: color.invert2.p56, marginTop: size.A },
    tutorContainer: { flex: 1, flexDirection: 'row', alignItems: 'center' },
    nameWrapper: {
        marginLeft: size.C,
        flexDirection: 'row',
        alignItems: 'center'
    },
    loaderWrapper: {
        height: size.K,
        width: size.K,
        borderRadius: 9999,
        backgroundColor: color.pending.ghost,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: size.C
    },
    badgeStyle: { backgroundColor: color.success.ghost },
    badgeText: { color: color.success.solid },
    subjectWrapper: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    searchingText: {
        color: color.invert2.p68
    }
}));
//# sourceMappingURL=Instant.js.map